// The search page's results from Algolia, so they match the header search. Until they arrive the
// page shows a placeholder grid (sections/main-search.liquid); Shopify's results are rendered too
// but stay hidden, and show only if Algolia fails (or without JavaScript). Results use the theme's
// card and pagination markup (snippets/card-product.liquid, snippets/pagination.liquid), with
// Load more.
//
// Shared helpers (algoliaQuery, algoliaPriceHtml, ...) come from algolia-search.js.
class AlgoliaSearchResults extends HTMLElement {
  connectedCallback() {
    this.config = algoliaConfig(this);
    this.icons = Object.fromEntries(
      [...this.querySelectorAll('template[data-algolia-icon]')].map((t) => [t.dataset.algoliaIcon, t.innerHTML.trim()]),
    );
    this.query = (new URLSearchParams(window.location.search).get('q') || '').trim();
    this.grid = document.getElementById('Product-Grid-Container');
    this.placeholder = document.querySelector('.algolia-results-placeholder');
    this.count = document.querySelector('.algolia-results-count');
    if (!this.grid || !this.query) return this.reveal();

    this.page = 0;
    this.grid.addEventListener('click', this.onGridClick.bind(this));
    this.search();
  }

  search({append = false} = {}) {
    algoliaQuery(this.config, [
      {
        indexName: this.config.productsIndex,
        params: {
          query: this.query,
          distinct: true,
          hitsPerPage: this.config.hitsPerPage,
          page: this.page,
          numericFilters: [`price<=${this.config.maxPrice}`],
          attributesToRetrieve: [...ALGOLIA_PRODUCT_ATTRIBUTES, 'meta.custom.badges'],
          attributesToHighlight: [],
          clickAnalytics: true,
          analyticsTags: ['search-page'],
        },
      },
    ])
      .then(([results]) => {
        this.renderCount(results.nbHits);
        this.renderGrid(results, append);
        this.reveal();
      })
      .catch((error) => {
        console.error(error);
        // Algolia is unavailable: keep what's shown, or show the results Shopify rendered.
        if (append) this.grid.querySelector('[data-load-more]')?.classList.remove('loading');
        else this.reveal();
      });
  }

  // Swaps the placeholder for the grid (Algolia's, or Shopify's if Algolia failed).
  reveal() {
    this.placeholder?.remove();
    this.grid?.removeAttribute('data-algolia-pending');
    this.count?.removeAttribute('data-algolia-pending');
  }

  renderCount(count) {
    if (!this.count) return;
    this.count.textContent = count
      ? algoliaPlural(this.config, 'results', count).replace('[terms]', this.query)
      : this.config.strings.noResults.replace('[terms]', this.query);
  }

  renderGrid(results, append) {
    const {strings} = this.config;
    if (!results.nbHits) {
      this.grid.innerHTML = `<div id="Paginate-Content" class="paginate-content--empty"><div class="empty-title">${escapeHtml(strings.empty)}</div></div>`;
      return;
    }
    const offset = results.page * results.hitsPerPage;
    const items = results.hits.map((hit, i) => this.gridItem(hit, offset + i + 1, results)).join('');

    if (append) {
      this.grid.querySelector('.content-list').insertAdjacentHTML('beforeend', items);
    } else {
      this.grid.innerHTML = `<div id="Paginate-Content" class="paginate-content">
          <ul class="content-list auto-grid list-unstyled" style="--item-min-size: ${this.config.minSize}; --item-min-size-mobile: ${this.config.minSizeMobile};">${items}</ul>
          <div class="pagination-container"></div>
        </div>`;
    }

    const viewed = Math.min(offset + results.hits.length, results.nbHits);
    const more = results.page + 1 < results.nbPages;
    this.grid.querySelector('.pagination-container').innerHTML = `
      <div class="pagination-progress">
        <div class="light">${escapeHtml(strings.viewed.replace('[count]', viewed).replace('[total]', results.nbHits))}</div>
        <div class="pagination-progress-bar" style="--progress: ${Math.round((viewed * 100) / results.nbHits)}%;"></div>
      </div>
      ${more ? `<button type="button" class="pagination-more-button button button--secondary has-loading button--hover-animate" data-load-more>
          <span class="loading-hidden">${escapeHtml(strings.loadMore)}</span>${this.icons.spinner}
        </button>` : ''}`;

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(this.grid);
  }

  gridItem(hit, position, results) {
    const {strings, quickAdd, ratioPercent, imageSizes} = this.config;
    const url = `/products/${encodeURIComponent(hit.handle)}`;
    const titleId = `Product-Title-algolia-${hit.id}`;
    const brand = this.config.showVendor !== false && algoliaBrand(hit);
    const reveal = this.config.revealOnScroll;
    const tracking = `data-algolia-object-id="${escapeHtml(hit.objectID)}" data-algolia-product-id="${escapeHtml(hit.id)}" data-algolia-position="${position}"
      data-algolia-query-id="${escapeHtml(results.queryID || '')}" data-algolia-index="${escapeHtml(results.index)}"`;
    const quick = quickAdd
      ? `<div class="card-quick-add">
          <modal-opener class="button button--icon button--pill button--hover-animate has-loading"
            data-product-handle="${escapeHtml(hit.handle)}" data-product-url="${url}" role="button" aria-haspopup="dialog"
            aria-expanded="false" aria-controls="Product-QuickView-Drawer" aria-label="${escapeHtml(strings.quickView)}" tabindex="0">
            <span class="loading-hidden d-inline-flex align-items-center gap-2">${this.icons.bag}</span>${this.icons.spinner}
          </modal-opener>
        </div>`
      : '';
    return `<li class="grid-item focus-inset${reveal ? ' scroll-trigger animate--slide-in' : ''}"${reveal ? ` data-cascade style="--animation-order: ${(position - 1) % this.config.hitsPerPage};"` : ''} tabindex="0">
        <div class="card-wrapper product-card-wrapper">
          <product-card class="card">
            <div class="card-inner ratio image-hover-effect--fade" style="--ratio-percent: ${ratioPercent}%;">
              <div class="card-media-wrapper" style="--alpha-background: 10%;">
                <div class="card-media media media-fit--cover">${shopifyImage(hit.product_image || hit.image, hit.title, [165, 360, 533, 720], imageSizes)}</div>
              </div>
              ${algoliaBadgesHtml(hit)}
              <a class="card-inner-content link" href="${url}" aria-labelledby="${titleId}" tabindex="-1" ${tracking}>
                <div class="card-information">${quick}</div>
              </a>
            </div>
            <div class="card-content text-align--left">
              <div class="card-others"></div>
              ${brand ? `<span class="product-vendor">${escapeHtml(brand)}</span>` : ''}
              <h3 class="card-heading font-body">
                <a id="${titleId}" class="link link-underline" href="${url}" aria-labelledby="${titleId}" ${tracking}>${escapeHtml(hit.title)}</a>
              </h3>
              <div class="card-price">${algoliaPriceHtml(hit, this.config)}</div>
            </div>
          </product-card>
        </div>
      </li>`;
  }

  onGridClick(event) {
    const more = event.target.closest('[data-load-more]');
    if (more) {
      event.preventDefault();
      more.classList.add('loading');
      this.page += 1;
      this.search({append: true});
      return;
    }
    algoliaClick(this.config, event, 'Search page result clicked');
  }
}

customElements.define('algolia-search-results', AlgoliaSearchResults);
